'use client';

import React from 'react';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';
import { cn } from '@/app/lib/utils';

type Props = {
  company: {
    id: string;
    name: string;
    logo: string;
    linkedin?: string;
    domain?: string;
  };
  titleClassName?: string;
  domainClassName?: string;
  avatarClassName?: string;
  initialsClassName?: string;
  avatarSize?: number;
  avatarImageClassName?: string;
  avatarImageSize?: number;
  wrapperClassName?: string;
};

const CompanyMiniCard = React.memo(
  ({
    company,
    titleClassName,
    domainClassName,
    initialsClassName,
    avatarClassName,
    avatarSize,
    avatarImageClassName,
    avatarImageSize,
    wrapperClassName,
  }: Props) => {
    const resolvedAvatarSize = avatarSize ?? (company.domain ? 28 : 24);
    const resolvedImageSize = avatarImageSize ?? resolvedAvatarSize;

    const openLinkedIn = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!company.linkedin) return;

      const url = company.linkedin.startsWith('http')
        ? company.linkedin
        : `https://${company.linkedin}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    const openDomain = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!company.domain) return;

      const url = company.domain.startsWith('http') ? company.domain : `https://${company.domain}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
      <div className={cn('flex min-w-0 items-center gap-2', wrapperClassName)}>
        <Avatar
          name={company.name || '-'}
          src={company.logo}
          isExternalUrl
          imageSize={resolvedImageSize}
          size={resolvedAvatarSize}
          className={cn('shrink-0 rounded-md', avatarClassName)}
          initialsClassName={cn('rounded-md !text-[9px]', initialsClassName)}
          imageClassName={cn('rounded-md', avatarImageClassName)}
        />
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="flex min-w-0 items-center gap-1">
            <span
              className={cn('min-w-0 truncate text-sm font-medium text-foreground', titleClassName)}
            >
              {company.name || '-'}
            </span>
            {company.linkedin ? (
              <button
                type="button"
                onClick={openLinkedIn}
                className="flex shrink-0 items-center text-[#0077B5]"
                aria-label={`${company.name} LinkedIn`}
              >
                <Icon name="linkedin-filled" size={4} />
              </button>
            ) : null}
          </div>
          {company.domain ? (
            <span
              onClick={openDomain}
              className={cn(
                'min-w-0 truncate text-xs text-muted-foreground hover:underline cursor-pointer',
                domainClassName
              )}
            >
              {company.domain}
            </span>
          ) : null}
        </div>
      </div>
    );
  }
);

CompanyMiniCard.displayName = 'CompanyMiniCard';

export default CompanyMiniCard;
